// Level marks, rows and chips. Every level reads as word + mark + colour —
// meaning never rides on colour alone, and home and school are never merged.
import React from 'react';
import { LEVELS } from '../hfpDisplay';
import { MARKS, PLACES } from '../icons';

const LEVEL_BG_VAR = {
  priority: '--hfp-level-now-bg',
  support: '--hfp-level-some-bg',
  well: '--hfp-level-well-bg',
  none: '--hfp-level-none-bg',
};

export function levelBg(levelKey) {
  return `var(${LEVEL_BG_VAR[levelKey] || LEVEL_BG_VAR.none})`;
}

export function Mark({ level, size = 18 }) {
  return (
    <img
      src={MARKS[level] || MARKS.none}
      alt=""
      aria-hidden="true"
      width={size}
      height={size}
      className="shrink-0 block"
    />
  );
}

export function PlaceIcon({ setting, size = 18 }) {
  return (
    <img
      src={PLACES[setting]}
      alt=""
      aria-hidden="true"
      width={size}
      height={size}
      className="shrink-0 block"
    />
  );
}

const SETTING_LABELS = { home: 'Home', school: 'School' };

/**
 * One row per setting on every area card, Home first then School.
 * On desktop the level word is a control that opens the meaning modal
 * (`onOpenMeaning`); on mobile the row is not a control.
 */
export function LevelRow({ setting, level, compact = false, onOpenMeaning }) {
  const info = LEVELS[level.key] || LEVELS.none;
  const word = info.word;
  return (
    <div
      className={`flex items-center w-full gap-[8px] px-[14px] ${compact ? 'h-[44px]' : 'h-[48px]'}`}
      style={{ background: levelBg(level.key) }}
    >
      <PlaceIcon setting={setting} size={compact ? 16 : 18} />
      <span className={`${compact ? 'hfp-meta-14' : 'hfp-body-16'} text-[color:var(--hfp-ink-mid)] whitespace-nowrap`}>
        {SETTING_LABELS[setting]}
      </span>
      <span className="flex-1 min-w-0" />
      <Mark level={level.key} size={compact ? 16 : 18} />
      {onOpenMeaning ? (
        // The whole row height (44/48px) is the hit area, extended to the row's
        // right edge, so the control meets the 44px touch-target floor — the
        // visible affordance stays the underlined word alone. Flush against the
        // card's overflow-hidden edge, so the focus ring is drawn inset.
        <button
          type="button"
          onClick={onOpenMeaning}
          className="hfp-focus-inset flex h-full min-w-[44px] shrink-0 items-center justify-end -mr-[14px] pr-[14px]"
        >
          <span className={`${compact ? 'hfp-meta-strong-14' : 'hfp-body-strong-16'} text-[color:var(--hfp-ink-strong)] underline whitespace-nowrap`}>
            {word}
          </span>
        </button>
      ) : (
        <span className={`${compact ? 'hfp-meta-strong-14' : 'hfp-body-strong-16'} text-[color:var(--hfp-ink-strong)] whitespace-nowrap`}>
          {word}
        </span>
      )}
    </div>
  );
}

/** Level as a chip: word + mark + colour (guide level map, flyout head). */
export function LevelChip({ level, underline = false }) {
  const info = LEVELS[level] || LEVELS.none;
  const word = level === 'none' ? 'Nothing recorded' : info.word;
  return (
    <span
      className="inline-flex items-center gap-[8px] px-[10px] py-[5px] rounded-[var(--hfp-radius-8)]"
      style={{ background: levelBg(level) }}
    >
      <Mark level={level} size={18} />
      <span className={`hfp-body-strong-16 text-[color:var(--hfp-ink-strong)] whitespace-nowrap ${underline ? 'underline' : ''}`}>
        {word}
      </span>
    </span>
  );
}
